import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ProspectStatusChip from './ProspectStatusChip';
import ReapprovalDialog from './ReapprovalDialog';
import { PROSPECT_SUBMISSION_STATUS } from '../../constants';
import {
  ProspectSelect,
  ProspectTextField,
  emptyProspectForm,
  formatSubmittedDate,
  prospectFormChanged,
  prospectFormSchema,
  toProspectForm,
  useClientProgramOptions,
} from './prospectFields';

const ProspectDetailsModal = ({ open, prospect, onClose, onSave, isSaving = false, companies, products }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);
  const methods = useForm({
    resolver: zodResolver(prospectFormSchema),
    defaultValues: emptyProspectForm,
  });
  const { handleSubmit, reset, watch, setValue, getValues } = methods;
  const clientId = watch('client');
  const productsReady = Array.isArray(products);
  const companiesReady = Array.isArray(companies);
  const productList = productsReady ? products : [];
  const companyList = companiesReady ? companies : [];
  const { clientOptions, programsForClient } = useClientProgramOptions(companyList, productList, clientId);

  useEffect(() => {
    if (!open || !prospect) return;
    reset(toProspectForm(prospect));
    setConfirmOpen(false);
    setPendingValues(null);
  }, [open, prospect, reset]);

  useEffect(() => {
    if (!open || !productsReady || !companiesReady) return;
    const currentProgram = getValues('program');
    const currentClient = getValues('client');
    if (!currentProgram || !currentClient) return;

    const company = companyList.find((item) => String(item?.client?._id || item?.client) === String(currentClient));
    const belongs = productList.some(
      (program) =>
        String(program._id) === String(currentProgram) &&
        String(program.company?._id || program.company) === String(company?._id),
    );
    if (!belongs) setValue('program', '');
  }, [open, productsReady, companiesReady, clientId, products, companies, getValues, setValue]);

  const clientSelectOptions = useMemo(
    () =>
      clientOptions.map((item) => ({
        value: item.value,
        label: item.companyName,
      })),
    [clientOptions],
  );

  const programSelectOptions = useMemo(() => {
    const options = programsForClient.map((program) => ({
      value: String(program._id),
      label: program.productName,
    }));
    const saved = prospect?.program;
    if (saved?._id && !options.some((option) => option.value === String(saved._id))) {
      const stillOriginalClient = String(prospect?.client?._id || '') === String(clientId);
      if (stillOriginalClient) {
        options.unshift({ value: String(saved._id), label: saved.productName || 'Program' });
      }
    }
    return options;
  }, [programsForClient, prospect, clientId]);

  const submitValues = (values) => {
    onSave(values, {
      onSuccess: () => {
        setConfirmOpen(false);
        setPendingValues(null);
      },
    });
  };

  const onSubmit = (values) => {
    if (!prospect || !prospectFormChanged(prospect, values)) {
      onClose();
      return;
    }
    if (prospect.status === PROSPECT_SUBMISSION_STATUS.APPROVED) {
      setPendingValues(values);
      setConfirmOpen(true);
      return;
    }
    submitValues(values);
  };

  const handleClose = () => {
    if (isSaving) return;
    setConfirmOpen(false);
    onClose();
  };

  if (!prospect) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth='md'>
        <FormProvider {...methods}>
          <Box component='form' onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Prospect details</DialogTitle>
            <DialogContent>
              <Typography variant='body2' color='text.secondary' mb={2.5}>
                View and edit the details of this prospect. You can make changes at any time.
              </Typography>
              <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                <ProspectTextField name='company' label='Company' required disabled={isSaving} />
                <ProspectTextField name='contactName' label='Contact name' required disabled={isSaving} />
                <ProspectTextField name='title' label='Title' disabled={isSaving} />
                <ProspectSelect
                  name='client'
                  label='Client'
                  required
                  placeholder='Select a client'
                  options={clientSelectOptions}
                  disabled={isSaving}
                />
                <ProspectSelect
                  name='program'
                  label='Program'
                  required
                  placeholder='Select a program'
                  options={programSelectOptions}
                  disabled={isSaving || !clientId}
                />
                <Box />
                <ProspectTextField
                  name='website'
                  label='Website'
                  disabled={isSaving}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <LinkIcon fontSize='small' />
                      </InputAdornment>
                    ),
                  }}
                />
                <ProspectTextField
                  name='linkedin'
                  label='LinkedIn'
                  disabled={isSaving}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <LinkedInIcon fontSize='small' color='primary' />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <ProspectTextField
                    name='howDoYouKnowThisPerson'
                    label='How do you know this person?'
                    multiline
                    minRows={3}
                    disabled={isSaving}
                  />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <ProspectTextField
                    name='whyRelevant'
                    label='Why could this be relevant?'
                    multiline
                    minRows={3}
                    disabled={isSaving}
                  />
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <ProspectTextField
                    name='anythingElse'
                    label='Anything else I should know?'
                    multiline
                    minRows={3}
                    disabled={isSaving}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between', alignItems: 'center' }}>
              <Box display='flex' alignItems='center' gap={3}>
                <Box>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Status
                  </Typography>
                  <ProspectStatusChip status={prospect.status} />
                </Box>
                <Box>
                  <Typography variant='caption' color='text.secondary' display='block'>
                    Date submitted
                  </Typography>
                  <Typography variant='body2'>{formatSubmittedDate(prospect.submittedAt)}</Typography>
                </Box>
              </Box>
              <Box display='flex' gap={1}>
                <Button onClick={handleClose} disabled={isSaving} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <Button type='submit' variant='contained' disabled={isSaving} sx={{ textTransform: 'none' }}>
                  Save changes
                </Button>
              </Box>
            </DialogActions>
          </Box>
        </FormProvider>
      </Dialog>
      <ReapprovalDialog
        open={confirmOpen}
        isSaving={isSaving}
        onKeepEditing={() => setConfirmOpen(false)}
        onConfirm={() => pendingValues && submitValues(pendingValues)}
      />
    </>
  );
};

export default ProspectDetailsModal;
